/** The API accepts both a generated script and an explicitly approved draft for rendering. */
export function canRenderEducationalDraft(status: unknown): boolean {
  return status === 'SCRIPT_READY' || status === 'APPROVED';
}

/** Reauthorizes the existing YouTube channel so Analytics consent stays bound to that account. */
export function youtubeAnalyticsAuthorizationUrl(accountId: unknown): string | null {
  const id = typeof accountId === 'string' ? accountId.trim() : '';
  return id ? `/api/social-accounts/youtube/oauth/start?accountId=${encodeURIComponent(id)}` : null;
}

export function canRetryEducationalTopic(status: unknown, updatedAt?: unknown, now = Date.now()): boolean {
  if (status === 'FAILED') return true;
  if (status !== 'SCRIPT_GENERATING' || typeof updatedAt !== 'string') return false;
  const updated = Date.parse(updatedAt);
  return Number.isFinite(updated) && now - updated >= 30 * 60_000;
}

export interface EducationalFactCheckReview {
  passed: boolean;
  notes: string | null;
  issues: Array<{ claim: string; issue: string; severity: string; sourceIds: string[] }>;
  unsupportedClaims: string[];
}

/** Bounded, display-safe summary for independent review details returned by the API. */
export function educationalFactCheckReview(value: unknown): EducationalFactCheckReview | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const text = (candidate: unknown) => typeof candidate === 'string' ? candidate.trim().slice(0, 600) : '';
  const issues = Array.isArray(row.issues) ? row.issues.slice(0, 10).flatMap((candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return [];
    const issue = candidate as Record<string, unknown>;
    const claim = text(issue.claim); const detail = text(issue.issue);
    if (!claim && !detail) return [];
    return [{ claim, issue: detail, severity: text(issue.severity), sourceIds: Array.isArray(issue.sourceIds) ? issue.sourceIds.flatMap((id) => typeof id === 'string' ? [id.slice(0, 120)] : []).slice(0, 8) : [] }];
  }) : [];
  const unsupportedClaims = Array.isArray(row.unsupportedClaims) ? row.unsupportedClaims.flatMap((claim) => { const value = text(claim); return value ? [value] : []; }).slice(0, 10) : [];
  return { passed: row.passed === true, notes: text(row.notes) || null, issues, unsupportedClaims };
}

export function educationalShortFactCheckFeedback(value: unknown): Array<{ slot: number; issue: string; claim: string; severity: string; notes: string }> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const report = value as Record<string, unknown>;
  if (report.status !== 'FAILED' || !Array.isArray(report.feedback)) return [];
  const text = (candidate: unknown, limit: number) => typeof candidate === 'string' ? candidate.trim().slice(0, limit) : '';
  return report.feedback.slice(0, 3).flatMap((candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return [];
    const row = candidate as Record<string, unknown>;
    const issues = Array.isArray(row.issues) ? row.issues : [];
    const note = text(row.notes, 400);
    const normalizedIssues = issues.slice(0, 5).flatMap((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
      const issue = item as Record<string, unknown>;
      const detail = text(issue.issue, 500);
      const claim = text(issue.claim, 320);
      return detail || claim ? [{ slot: Number(row.slot) || 0, issue: detail, claim, severity: text(issue.severity, 32), notes: note }] : [];
    });
    if (normalizedIssues.length) return normalizedIssues;
    const unsupported = Array.isArray(row.unsupportedClaims) ? row.unsupportedClaims.slice(0, 5) : [];
    return unsupported.flatMap((claim) => {
      const detail = text(claim, 320);
      return detail ? [{ slot: Number(row.slot) || 0, issue: 'This claim was not supported by the verified source pack.', claim: detail, severity: 'review', notes: note }] : [];
    });
  }).slice(0, 12);
}

function normalizeTopicTitle(value: unknown): string {
  return String(value ?? '').normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/** Matches a discovery suggestion to a persisted topic without fuzzy false positives. */
export function findExistingEducationalTopic(title: string, topics: unknown[]): Record<string, any> | null {
  const normalizedTitle = normalizeTopicTitle(title);
  if (!normalizedTitle) return null;
  return (topics as Array<Record<string, any>>).find((topic) => normalizeTopicTitle(topic?.title) === normalizedTitle) ?? null;
}

/** Stable display order for polled topic cards; progress updates must not reorder them. */
export function sortEducationalTopicsForDisplay<T extends Record<string, any>>(topics: T[]): T[] {
  return [...topics].sort((left, right) => {
    const scoreDifference = Number(right.scores?.overall_score ?? right.overall_score ?? 0) - Number(left.scores?.overall_score ?? left.overall_score ?? 0);
    if (scoreDifference) return scoreDifference;
    const leftCreated = Date.parse(String(left.createdAt ?? ''));
    const rightCreated = Date.parse(String(right.createdAt ?? ''));
    if (Number.isFinite(leftCreated) && Number.isFinite(rightCreated) && leftCreated !== rightCreated) return leftCreated - rightCreated;
    return String(left.id ?? '').localeCompare(String(right.id ?? ''));
  });
}

export function educationalLongformUsage(topic: Record<string, any> | null): { label: string; used: boolean; published: boolean } {
  if (!topic) return { label: 'Nieutworzono', used: false, published: false };
  if (topic.youtubeUrl || topic.status === 'PUBLISHED') return { label: 'Opublikowany', used: true, published: true };
  if (topic.videoJobId && topic.status === 'READY') return { label: 'Wideo gotowe', used: true, published: false };
  if (topic.videoJobId && topic.status === 'RENDERING') return { label: 'Render w toku', used: true, published: false };
  if (topic.videoJobId && topic.status === 'FAILED') return { label: 'Render nieudany', used: true, published: false };
  if (topic.videoJobId) return { label: 'Wideo utworzone', used: true, published: false };
  if (topic.status === 'IDEA') return { label: 'W kolejce · bez MP4', used: false, published: false };
  if (topic.status === 'FAILED') return { label: 'Produkcja nieudana · bez MP4', used: false, published: false };
  if (topic.status === 'REJECTED') return { label: 'Odrzucony · bez MP4', used: false, published: false };
  if (topic.status) return { label: 'Temat w produkcji · bez MP4', used: false, published: false };
  return { label: 'Nieutworzono', used: false, published: false };
}

export function hasGeneratedEducationalLongform(topic: Record<string, any> | null): boolean {
  if (!topic) return false;
  return Boolean(topic.youtubeUrl || topic.status === 'PUBLISHED' || (topic.videoJobId && ['READY', 'APPROVED'].includes(String(topic.status))));
}

/** Summarizes whether Shorts have finished rendering for the topic queue. */
export function educationalShortsUsage(shorts: unknown): { label: string; ready: number; total: number; complete: boolean } {
  if (!Array.isArray(shorts) || shorts.length === 0) return { label: 'Nieutworzone', ready: 0, total: 0, complete: false };
  const ready = shorts.filter((item: any) => ['READY', 'QUEUED', 'PUBLISHED'].includes(String(item?.status))).length;
  const total = shorts.length;
  if (ready === total) return { label: `Utworzone · ${ready}/${total}`, ready, total, complete: true };
  if (ready > 0) return { label: `Częściowo · ${ready}/${total}`, ready, total, complete: false };
  return { label: `W przygotowaniu · 0/${total}`, ready, total, complete: false };
}

/** Gives renderer-queue state precedence over the broader Short workflow state. */
export function educationalShortStatusLabel(short: unknown): string {
  if (!short || typeof short !== 'object' || Array.isArray(short)) return 'W PRZYGOTOWANIU';
  const row = short as Record<string, unknown>;
  if (row.status === 'PUBLISHED') return 'PUBLISHED';
  if (row.status === 'QUEUED') return 'QUEUED';
  if (row.renderStatus === 'QUEUED') return 'RENDER QUEUED';
  if (['CLAIMED', 'PREPARING', 'RENDERING', 'UPLOADING'].includes(String(row.renderStatus))) return 'RENDERING';
  return String(row.status ?? 'W PRZYGOTOWANIU').replaceAll('_', ' ');
}

export interface EducationalScenePreview {
  sceneId: string;
  status: 'QUEUED' | 'GENERATING' | 'GENERATED' | 'FALLBACK';
  imageUrl: string | null;
  error: string | null;
}

export function getEducationalScenePreviews(visualPlan: unknown): EducationalScenePreview[] {
  if (!Array.isArray(visualPlan)) return [];
  const supported = new Set<EducationalScenePreview['status']>(['QUEUED', 'GENERATING', 'GENERATED', 'FALLBACK']);
  return visualPlan.flatMap((scene: unknown, index: number) => {
    if (!scene || typeof scene !== 'object') return [];
    const row = scene as Record<string, unknown>;
    const asset = row.imageAsset && typeof row.imageAsset === 'object' ? row.imageAsset as Record<string, unknown> : {};
    if (!supported.has(String(asset.status) as EducationalScenePreview['status'])) return [];
    const status = String(asset.status) as EducationalScenePreview['status'];
    const imageUrl = typeof row.imageUrl === 'string' ? row.imageUrl : typeof asset.url === 'string' ? asset.url : null;
    return [{ sceneId: String(asset.sceneId ?? row.sceneId ?? `SCENE ${index + 1}`), status, imageUrl, error: typeof asset.error === 'string' ? asset.error : null }];
  });
}

export function summarizeEducationalScenePreviews(scenes: EducationalScenePreview[]) {
  return {
    total: scenes.length,
    generated: scenes.filter((scene) => scene.status === 'GENERATED').length,
    inProgress: scenes.filter((scene) => scene.status === 'QUEUED' || scene.status === 'GENERATING').length,
    fallback: scenes.filter((scene) => scene.status === 'FALLBACK').length,
  };
}
